import React from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';

import {
  EMPTY_CALENDAR_FILTERS,
  FILTER_SECTIONS,
  getFilterOptionLabel,
  getSelectedFilterCount,
  toggleSelectedId,
} from '../calendarFilters.helpers';
import './CalendarFiltersPanel.scss';

export function CalendarFiltersPanel({ filters, onApply, pmas, members, onClose }) {
  const { t } = useTranslation();
  const [draft, setDraft] = React.useState(filters);
  const [section, setSection] = React.useState(FILTER_SECTIONS.PMAS);
  const [query, setQuery] = React.useState('');
  React.useEffect(() => setDraft(filters), [filters]);
  const toggleFilterValue = React.useCallback((field, id) => {
    setDraft((current) => ({ ...current, [field]: toggleSelectedId(current[field], id) }));
  }, []);
  const isPmaSection = section === FILTER_SECTIONS.PMAS;
  const options = isPmaSection ? pmas : members;
  const field = isPmaSection ? 'pmaIds' : 'memberIds';
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleOptions = React.useMemo(
    () => options.filter((option) => getFilterOptionLabel(option).toLocaleLowerCase().includes(normalizedQuery)),
    [normalizedQuery, options]
  );
  const hasSelectedFilters = getSelectedFilterCount(draft) > 0;

  const clearFilters = () => {
    onApply({
      ...EMPTY_CALENDAR_FILTERS,
      pmaIds: [],
      memberIds: [],
    });
  };

  return (
    <div className="calendar-filters-panel" role="dialog" aria-modal="false" aria-label={t('calendar-filters', { defaultValue: 'Filters' })}>
      <header className="calendar-filters-panel__header">
        <h3>{t('calendar-filters', { defaultValue: 'Filters' })}</h3>
        <button type="button" onClick={onClose} aria-label={t('common-close', { defaultValue: 'Close' })}>
          <CloseIcon fontSize="small" />
        </button>
      </header>
      <div className="calendar-filter-chips">
        {draft.completedOnly && (
          <button type="button" onClick={() => setDraft((current) => ({ ...current, completedOnly: false }))}>
            {t('calendar-completed', { defaultValue: 'Completed' })} <CloseIcon fontSize="inherit" />
          </button>
        )}
        {['pmaIds', 'memberIds'].flatMap((key) => draft[key].map((id) => {
          const item = (key === 'pmaIds' ? pmas : members).find((option) => String(option.id) === id);
          return (
            <button key={`${key}-${id}`} type="button" onClick={() => toggleFilterValue(key, id)}>
              {getFilterOptionLabel(item)} <CloseIcon fontSize="inherit" />
            </button>
          );
        }))}
      </div>
      <div className="calendar-filters-panel__body">
        <nav aria-label={t('calendar-filter-types', { defaultValue: 'Filter types' })}>
          <button
            type="button"
            className={draft.completedOnly ? 'is-selected' : ''}
            aria-pressed={draft.completedOnly}
            onClick={() => setDraft((current) => ({ ...current, completedOnly: !current.completedOnly }))}
          >
            <TaskAltOutlinedIcon fontSize="small" />
            <span>{t('calendar-completed-tasks', { defaultValue: 'Completed tasks' })}</span>
            <span className="calendar-filters-panel__section-count">({draft.completedOnly ? 1 : 0})</span>
          </button>
          <button type="button" className={isPmaSection ? 'active' : ''} onClick={() => { setSection(FILTER_SECTIONS.PMAS); setQuery(''); }}>
            <PersonOutlineOutlinedIcon fontSize="small" />
            <span>{t('calendar-assigned-pma', { defaultValue: 'Assigned PMA' })}</span>
            <span className="calendar-filters-panel__section-count">({draft.pmaIds.length})</span>
          </button>
          <button type="button" className={!isPmaSection ? 'active' : ''} onClick={() => { setSection(FILTER_SECTIONS.MEMBERS); setQuery(''); }}>
            <PersonOutlineOutlinedIcon fontSize="small" />
            <span>{t('calendar-rrc-members', { defaultValue: 'RRC Members' })}</span>
            <span className="calendar-filters-panel__section-count">({draft.memberIds.length})</span>
          </button>
        </nav>
        <section>
          <strong>{isPmaSection ? t('calendar-assigned-pma', { defaultValue: 'Assigned PMA' }) : t('calendar-rrc-members', { defaultValue: 'RRC Members' })}</strong>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('common-search-by-name', { defaultValue: 'Search by name' })} />
          <div className="calendar-filter-options">
            {visibleOptions.map((option) => {
              const id = String(option.id);
              return (
                <label key={id}>
                  <input type="checkbox" checked={draft[field].includes(id)} onChange={() => toggleFilterValue(field, id)} />
                  {getFilterOptionLabel(option)}
                </label>
              );
            })}
            {visibleOptions.length === 0 && <p>{t('common-no-results', { defaultValue: 'No results' })}</p>}
          </div>
        </section>
      </div>
      <footer className="calendar-filters-panel__actions">
        <button
          type="button"
          className="calendar-filters-panel__clear"
          onClick={clearFilters}
          disabled={!hasSelectedFilters}
        >
          {t('calendar-clean-filters', { defaultValue: 'Clean filters' })}
        </button>
        <button type="button" className="calendar-filters-panel__apply" onClick={() => onApply(draft)}>
          {t('calendar-select-filters', { defaultValue: 'Select filters' })}
        </button>
      </footer>
    </div>
  );
}
